import { tpoPartTestId } from '../../config/testIds';
import { formatPromoScalar, isCellValue } from './utils/promoCellValue';
import type { KpiViewType, MetricType, PromoMetricCell } from './types';

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

export const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '--';
    return NUMBER_FORMATTER.format(value);
};

export const renderNumberCell = (value: number | null | undefined, testId?: string) => (
    <span data-testid={testId ? tpoPartTestId(testId, 'value') : undefined}>{formatNumber(value)}</span>
);

interface PromoCellProps {
    value: PromoMetricCell;
    kpiViewType: KpiViewType;
    metricType: MetricType;
    /** Owning cell's test id; the current/change/previous parts derive from it. */
    testId?: string;
}

const isComparisonView = (kvt: KpiViewType): boolean => kvt === 'actuals_vs_pre_snapshot';

const formatChange = (change: number | null, metricType: MetricType): string => {
    if (change === null || change === undefined || Number.isNaN(change)) return '--';
    const formatted = NUMBER_FORMATTER.format(Math.abs(change));
    return metricType === 'percent' ? `${formatted}%` : formatted;
};

const arrowFor = (change: number | null): string => {
    if (change === null || change === undefined) return '';
    if (change > 0) return '\u2191';
    if (change < 0) return '\u2193';
    return '';
};

const directionFor = (change: number | null): 'up' | 'down' | 'flat' | undefined => {
    if (change === null || change === undefined) return undefined;
    if (change > 0) return 'up';
    if (change < 0) return 'down';
    return 'flat';
};

const colourFor = (change: number | null): string => {
    if (change === null || change === undefined || change === 0) return '#52525C';
    return change > 0 ? '#15803D' : '#B91C1C';
};

export const PromoCell = ({ value, kpiViewType, metricType, testId }: PromoCellProps) => {
    const currentTestId = testId ? tpoPartTestId(testId, 'current') : undefined;
    if (!isCellValue(value)) return <span data-testid={currentTestId}>--</span>;
    if (!isComparisonView(kpiViewType)) {
        return <span data-testid={currentTestId}>{formatNumber(value.current)}</span>;
    }

    const arrow = arrowFor(value.change);
    return (
        <div className="flex items-center justify-end gap-x-3">
            <span className="text-[#18181B] font-medium" data-testid={currentTestId}>
                {formatNumber(value.current)}
            </span>
            <span
                style={{ color: colourFor(value.change) }}
                className="font-medium inline-flex items-center gap-x-1"
                data-testid={testId ? tpoPartTestId(testId, 'change') : undefined}
                data-state-direction={directionFor(value.change)}
            >
                {value.change === null
                    ? '--'
                    : `${formatNumber(Math.abs(value.change))}${metricType === 'percent' ? '%' : ''}`}
                {arrow ? <span>{arrow}</span> : null}
            </span>
            <span className="text-[#A1A1AA]" data-testid={testId ? tpoPartTestId(testId, 'previous') : undefined}>
                {formatNumber(value.previous)}
            </span>
        </div>
    );
};

PromoCell.displayName = 'PromoCell';

interface PromoScalarCellProps {
    value: PromoMetricCell;
    testId?: string;
}

export const PromoScalarCell = ({ value, testId }: PromoScalarCellProps) => (
    <span data-testid={testId ? tpoPartTestId(testId, 'current') : undefined}>{formatPromoScalar(value)}</span>
);

PromoScalarCell.displayName = 'PromoScalarCell';

// Used for static plan-metric tooltip strings (non-component).
export { formatChange };
